import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { AlertTriangle, ArrowRight, ClipboardList, Loader2, Lock, Mail, Receipt, Truck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { BrandLogo } from "@/components/common/Brand";
import { useAuth } from "@/context/AuthContext";
import { errorMessage } from "@/lib/api";

/** Konto for dagleg leiar – ferdig utfylt for rask start. */
const PREFILLED = { email: "kjellaage@ekeland-transport.no", password: "Demo1234!" };

const STEPS = [
  { icon: ClipboardList, title: "Opprett og tildel", text: "Du lagar oppdraget frå mal og tildeler mannskap." },
  { icon: Truck, title: "Registrer i felt", text: "Timar, maskin, massar og bilete – rett frå mobilen, på under eitt minutt." },
  { icon: Receipt, title: "Kontroller og fakturer", text: "Godkjend revisjon blir låst, eksportert som PDF/CSV og merkt fakturert." },
];

const homeFor = (u) => (u.role === "administrasjon" ? "/ordrar" : "/mine-ordrar");

export default function LoginPage() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState(PREFILLED.email);
  const [password, setPassword] = useState(PREFILLED.password);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  if (!loading && user) {
    return <Navigate to={homeFor(user)} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const u = await login(email.trim(), password);
      navigate(homeFor(u), { replace: true });
    } catch (err) {
      setError(errorMessage(err, "Innlogginga feila. Kontroller e-post og passord."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-background lg:flex-row" data-testid="login-page">
      {/* Merkevarepanel */}
      <aside className="surface-charcoal relative flex flex-col lg:w-[46%] lg:min-h-screen xl:w-[44%]" data-testid="login-brand-panel">
        <div className="brand-rule" aria-hidden="true" />
        <div className="flex flex-1 flex-col px-6 pb-8 pt-7 sm:px-10 lg:px-14 lg:pb-12 lg:pt-12">
          <div className="flex items-center justify-between gap-4">
            <BrandLogo className="w-[200px] sm:w-[232px] lg:w-[260px]" data-testid="login-brand-logo" />
          </div>

          <div className="mt-10 hidden flex-1 flex-col justify-center lg:flex">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-red">Internt ordresystem</p>
            <h1 className="mt-3 max-w-xl text-4xl font-semibold leading-[1.1] tracking-tight text-white xl:text-5xl">
              Frå oppdrag til fakturagrunnlag – på éin stad.
            </h1>
            <p className="mt-5 max-w-lg text-base leading-7 text-brand-silver">
              Eitt verktøy for kontor og felt. Mannskapet registrerer på mobilen, kontoret kontrollerer, godkjenner og eksporterer – utan dobbeltføring.
            </p>

            <ol className="mt-10 space-y-5" data-testid="login-flow-steps">
              {STEPS.map((s, i) => (
                <li key={s.title} className="flex gap-4">
                  <span className="relative mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-charcoal-2 text-brand-silver">
                    <s.icon className="h-5 w-5" />
                    <span className="num absolute -left-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-brand-red text-[11px] font-semibold text-white">
                      {i + 1}
                    </span>
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-white">{s.title}</p>
                    <p className="mt-0.5 text-sm leading-6 text-brand-silver">{s.text}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          <div className="mt-6 flex flex-col gap-2 text-sm text-brand-silver lg:mt-10 lg:flex-row lg:items-end lg:justify-between">
            <p className="font-medium text-white">Kvalitet i tre generasjonar sidan 1935</p>
            <p className="text-xs">Hatlestrandsvegen 667, 5635 Hatlestrand</p>
          </div>
        </div>
      </aside>

      {/* Skjema */}
      <main className="flex flex-1 flex-col items-center justify-center px-4 py-8 sm:px-8 lg:py-12" data-testid="login-form-panel">
        <div className="fade-in-up w-full max-w-[440px]">
          <div className="rounded-xl border border-border bg-card p-6 sm:p-8" data-testid="login-card">
            <h2 className="text-2xl font-semibold tracking-tight">Logg inn</h2>
            <p className="mt-1 text-sm text-muted-foreground">Velkomen tilbake. Bruk firma-e-posten din.</p>

            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="login-form" noValidate>
              <div className="space-y-2">
                <Label htmlFor="email">E-post</Label>
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                  <Input
                    id="email"
                    type="email"
                    inputMode="email"
                    autoComplete="username"
                    autoCapitalize="none"
                    spellCheck={false}
                    required
                    placeholder="namn@ekeland-transport.no"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-12 bg-card pl-10 text-base"
                    data-testid="login-email-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Passord</Label>
                <div className="relative">
                  <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                  <Input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-12 bg-card pl-10 text-base"
                    data-testid="login-password-input"
                  />
                </div>
              </div>

              {error ? (
                <Alert variant="destructive" data-testid="login-error" className="bg-card" role="alert">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              ) : null}

              <Button type="submit" className="press h-12 w-full text-base" disabled={busy} data-testid="login-submit-button">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {busy ? "Loggar inn …" : "Logg inn"}
                {!busy ? <ArrowRight className="h-4 w-4" /> : null}
              </Button>
            </form>
          </div>

          <p className="mt-4 px-1 text-center text-xs leading-5 text-muted-foreground" data-testid="login-help-note">
            Problem med innlogginga? Kontakt administrasjonen for nytt passord.
          </p>
        </div>
      </main>
    </div>
  );
}
